import { Table } from "@bbri/ui";
import { AxiosResponse } from "axios";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { InputTextarea } from 'primereact/inputtextarea';
import { Toast } from "primereact/toast";
import { Tooltip } from 'primereact/tooltip';
import React from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import { SeverityEnum } from "../../shared/enum/SeverityEnum";

import Editor from "@monaco-editor/react";
import * as monaco from 'monaco-editor/esm/vs/editor/editor.api';

import { AdminScriptsService } from "../services/AdminScriptsService";
import { ScriptDTO } from "../dto/ScriptDTO";

import CodeSidebarJS from "./components/JS/CodeSidebarJS";
import QuestionSelectorJS from "./components/JS/QuestionSelectorJS";
import "./CodeEditor.scss";
import { Toolbar } from "primereact/toolbar";
import { InputText } from "primereact/inputtext";
import {Dialog} from "primereact/dialog";

interface Props extends WithTranslation {
  onDirtyChange: any;
}

interface States {
  displayLeaveConfirmation: boolean;
  displayRenameScript: boolean;
  displayConfirmation: boolean;
  displayScriptTableLog: boolean;
  displayScriptSelectedLog: boolean;
  displayNewScriptConfirmation: boolean;
  displayScriptInfo: boolean;
  selectedError: number;
  editorText: string;
  editorScriptName: string;
  actualScriptName: string;
  scriptErrorLogs: any;
  isRenameRequest: boolean;
  refreshScriptsState: boolean;
  editorRef: React.MutableRefObject<monaco.editor.IStandaloneCodeEditor | null>;
  deleteScript: boolean;
  downloadScript: boolean;
  isDirty: boolean;
  isLoading: boolean,
  nextScript: string | null;
}


class CodeEditorJS extends React.Component<Props, States> {


  private toast: Toast | null;

  private readonly scriptService: AdminScriptsService;

  private leftContents: any;
  private rightContents: any;

  constructor(props: Props) {
    super(props);
    this.toast = null;

    // Languages
    this.scriptService = new AdminScriptsService();

    this.state = {
      displayConfirmation: false,
      displayRenameScript: false,
      displayScriptTableLog: false,
      displayScriptSelectedLog: false,
      editorText: "",
      editorScriptName: "",
      actualScriptName: "",
      scriptErrorLogs: {},
      isRenameRequest: false,
      displayNewScriptConfirmation: false,
      displayScriptInfo: false,
      refreshScriptsState: false,
      editorRef: React.createRef<monaco.editor.IStandaloneCodeEditor | null>(),
      deleteScript: false,
      downloadScript: false,
      displayLeaveConfirmation: false,
      isDirty: false,
      isLoading: true,
      nextScript: null,
      selectedError: -999,
    };
    this.handleEditorChange = this.handleEditorChange.bind(this);
    this.resetEditorText = this.resetEditorText.bind(this);
    this.handleOneUpload = this.handleOneUpload.bind(this);
    this.handleEditorDidMount = this.handleEditorDidMount.bind(this);
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.isDirty !== this.state.isDirty) {
      this.props.onDirtyChange(this.state.isDirty);
    }
  }

  componentDidMount() {
    window.addEventListener('beforeunload', this.handleBeforeUnload);
  }

  componentWillUnmount() {
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
  }

  handleBeforeUnload = (e) => {
    if (this.state.isDirty) {
      e.preventDefault();
      e.returnValue = 'Are you sure you want to leave this page? Unsaved changes will be lost.';
    }
  }

  requestNavigation = (nextScriptName) => {
    if (this.state.isDirty) {
      this.setState({
        displayLeaveConfirmation: true,
        nextScript: nextScriptName
      });
    } else {
      this.refreshScript(nextScriptName);
    }
  }

  confirmLeaveWithoutSaving = () => {
    this.setState({ displayLeaveConfirmation: false }, () => {
      if (this.state.nextScript) {
        this.refreshScript(this.state.nextScript);
      }
    });
  }

  cancelLeave = () => {
    this.setState({ displayLeaveConfirmation: false });
  }

  refreshScript = (scriptToView) => {
    this.setState({
      isLoading: true,
      editorScriptName: scriptToView,
      actualScriptName: scriptToView,
      isDirty: false,
      nextScript: null });
    this.getScriptToViewText(scriptToView);
  };

  // Languages
  getScriptToViewText(str: string) {
    if (!str || !str.includes(".js")) return;

    this.scriptService.getOneLogsError(str).then((response: any) => {
      const sortedLogs = response.data.sort(
        (a, b) => { return new Date(b.date).getTime() - new Date(a.date).getTime(); }
      );

      this.setState({ scriptErrorLogs: sortedLogs })
    })

    this.scriptService.getOne(str).then((response: AxiosResponse<string>) => {
      this.setState({
        editorText: response.data,
        editorScriptName: str,
        isLoading: false });
    });
  }


  /**
   * On upload for one script
   */
  handleOneUpload() {
    let actualName = this.state.actualScriptName;
    let isRenameRequest = this.state.isRenameRequest
    let newName = this.state.editorScriptName;

    if (!newName) {
      this.toast?.show({
        severity: SeverityEnum.ERROR,
        detail: `Please enter a script name !`,
      });
      return;
    }

    let extensionPart = newName.substring(newName.lastIndexOf('.'));

    const allowedTypes = ['.js'];

    // = Validate the types
    if (!allowedTypes.includes(extensionPart)) {
      newName += ".js";
    }

    let dto: ScriptDTO;

    if (isRenameRequest) {
      dto = new ScriptDTO(actualName, newName, this.state.editorText, isRenameRequest);
      this.setState({isRenameRequest: false})
    }
    else {
      dto = new ScriptDTO(actualName, newName, this.state.editorText);
    }

    this.scriptService
      .uploadOne(dto)
      .then(() => {
        this.toast?.show({
          severity: SeverityEnum.SUCCESS,
          detail: 'Script sauvegardé !',
        });

        this.setState({ refreshScriptsState: true, isDirty: false });
        this.setState({ refreshScriptsState: false });

      })
      .catch((err: any) => {
        const message = err?.response?.data?.message;
        this.toast?.show({
          severity: SeverityEnum.ERROR,
          detail: `Une erreur est survenue lors de la sauvegarde du script: ${message}`,
        });
      });
  }


  resetEditorText() {
    this.setState({
      editorText: '',
      displayNewScriptConfirmation: false,
      editorScriptName: "",
      actualScriptName: "",
    });

  }


  /*
    Actions handeling
   */
  handleEditorChange(value, event) {
    if (value !== this.state.editorText) {
      if (!this.state.isDirty) {
        this.setState({ isDirty: true });
      }
    }

    this.setState({ editorText: value });

  }

  /*
    Validate methods
  */
  validationWindowForNewScript() {
    return (
      <div>
        <Button
          label="No"
          icon="pi pi-times"
          onClick={() => this.setState({ displayNewScriptConfirmation: false })}
          className="p-button-text"
        />
        <Button
          label="Yes"
          icon="pi pi-check"
          onClick={() => this.resetEditorText()}
          autoFocus
        />
      </div>
    );
  }


  handleDeletedScript = () => {

    this.resetEditorText();

  };


  handleEditorDidMount(editor, monaco) {

    this.state.editorRef.current = editor;

  }

  selectQuestion = (questionSelected) => {

    this.state.editorRef.current?.trigger('keyboard', 'type', { text: 'var answer = responses.get("' + questionSelected + '");' });

  };

  renderLeavingPageDialog() {
    return (
        <div>
          <Button label="Yes" icon="pi pi-check" onClick={this.confirmLeaveWithoutSaving}/>
          <Button label="No" icon="pi pi-times" onClick={this.cancelLeave}/>
        </div>
    );
  }

  loadFirstScript = (fileName) => {
    this.setState({ isLoading: true }, () => {
      this.refreshScript(fileName);
    });
  };

  showScriptInfoModal = () => {
    this.setState({ displayScriptInfo: true });
  };

  hideScriptInfoModal = () => {
    this.setState({ displayScriptInfo: false });
  };

  copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      this.toast?.show({ severity: 'success', detail: `Copied: ${text}` });
    }).catch(() => {
      this.toast?.show({ severity: 'error', detail: `Failed to copy: ${text}` });
    });
  };

  private _deleteErrorLogs() {
    this.scriptService.uploadOneWithoutLogsError(this.state.actualScriptName)
      .then(() => { this.setState({ scriptErrorLogs: [] }); })
      .catch((e) => { console.error('Error deleting log:', e); })
  }

  private _renderRenameWindowFooterForScript() {
    return (
      <div>
        <Button
          label="No"
          icon="pi pi-times"
          onClick={() => this.setState({
            displayRenameScript: false ,
            isRenameRequest: false,
            editorScriptName: this.state.actualScriptName
          })}
          style={{ backgroundColor: '#0087b7', borderColor: '#0087b7' }}
          className="p-button-danger"
        />
        <Button
          label="Yes"
          icon="pi pi-check"
          onClick={() => {
            this.setState({
              displayRenameScript: false,
            })
            this.handleOneUpload();
          }}
          autoFocus
          className="p-button-danger"
          style={{ backgroundColor: '#e98b3a', borderColor: '#e98b3a' }}
        />
      </div>
    );
  }

  //region LOGS BUTTON RENDERING

  private _renderLogsButton() {
    return (
      <>
        <Button
          tooltip="View error logs for this script"
          tooltipOptions={{ position: 'left' }}
          icon="pi pi-exclamation-triangle"
          style={{
            backgroundColor: 'rgba(0, 145, 152)',
            borderColor: 'rgba(0, 145, 152)' ,
            position: "relative"
          }}
          className="logs"
          onClick={ () =>
            this.setState({
              displayScriptTableLog: true
            })
          }
        />
        { this._renderLogsIndicator() }
        { this._renderLogsDialog() }
      </>

    )
  }

  private _renderLogsIndicator() {
    const isScriptErrorDetected = this.state.scriptErrorLogs.length > 0

    return (
      isScriptErrorDetected
           && (
             <div className="logs-indicator">
               <i
                 className="pi pi-exclamation-circle"
                 style={{
                   fontSize: '8px',
                   color: 'white',
                 }}
               />
             </div>
           )

    )
  }

  private _renderLogsDialog() {
    const errors = this.state.scriptErrorLogs
    let dates = []
    let errorMessages = []

    if (errors.length > 0) { //if error
      errorMessages = []
      dates = errors.map((error: any) => new Date(error.date).toLocaleString());
      errorMessages = errors.map((error: any) => error.errorMessage);
    }

    return (
      <>
        <Dialog
          header="Logs error"
          visible ={ this.state.displayScriptTableLog }
          modal
          style={{ width: '650px' }}
          footer={this._renderSpecificLogErrorFooter()}
          onHide={() =>
            this.setState(
              { displayScriptTableLog: false }
            )
          }
        >
          <Table>
            <thead>
            <tr>
              <th className="">Dates</th>
              <th className="">Subjects</th>
            </tr>
            </thead>
            <tbody>
              {errors.length > 0 ? (
                // if errors
                errors.map((error: any, index: number) => (
                  <tr
                    key={index}
                    onClick={() => {
                      this.setState({
                        displayScriptSelectedLog: true,
                        selectedError: index
                      })
                    }}
                  >
                    <td>{new Date(error.date).toLocaleString()}</td>
                    <td>{error.errorMessage}</td>
                  </tr>
                ))
              ) // else
                : (
                <tr>
                  <td></td>
                  <td>any error found</td>
                </tr>
              )}
            </tbody>
          </Table>
        </Dialog>
      { this._renderSpecificLogError() }
      </>
    )
  }

  private _renderSpecificLogError() {
    const errors = this.state.scriptErrorLogs;
    const index = this.state.selectedError;

    return (
      <Dialog
        header="Logs error"
        visible ={ this.state.displayScriptSelectedLog }
        modal
        style={{ width: '850px' }}
        onHide={() =>
        this.setState(
          { displayScriptSelectedLog: false }
        ) }
  >
        <InputTextarea
          className="conditionTextArea"
          value={
            index !== null && errors[index]
              ? JSON.stringify( ( ({ _id, ...rest }) => rest )(errors[index]), null, 2 )
              : ""
          }
          rows={25} cols={60}
          style={ { width:"100%" } }
        />
      </Dialog>

    )
  }

  private _renderSpecificLogErrorFooter() {
    return (
      <div>
        <Button
          tooltip="delete logs"
          tooltipOptions={{ position: 'left' }}
          label="Clear"
          icon="pi pi-trash"
          onClick={() => { this._deleteErrorLogs(); }}
          autoFocus
          className="p-button-danger"
        />
      </div>
    );
  }

  //endregion

  render() {
    const scriptLines = [
      { text: 'Script structure', isComment: true },
      { text: 'var answer = responses.get("QUESTION_NAME");', isComment: false, desc: 'Fetches the answer for a specific question.' },
      { text: 'data.result.push(answer);', isComment: false, desc: 'Adds the answer to the result list.' },
      { text: 'data.result = answer;', isComment: false, desc: 'Sets the answer directly as the result.' },
      { text: 'Using customProps', isComment: true },
      { text: 'data.result = customProps ? customProps.desiredProperty : null;', isComment: false, desc: 'Assigns a specific property from customProps if available.' },
      { text: 'Using submissionProps (only in Word script)', isComment: true },
      { text: 'submissionProps.submission_id;', isComment: false, desc: 'Unique ID of the submission.' },
      { text: 'submissionProps.userLastname;', isComment: false, desc: 'Last name of the user.' },
      { text: 'submissionProps.userFirstname;', isComment: false, desc: 'First name of the user.' },
      { text: 'submissionProps.userEmail;', isComment: false, desc: 'Email of the user.' },
      { text: 'submissionProps.timestamp;', isComment: false, desc: 'Timestamp of the submission.' },
      { text: 'Verify user connection state', isComment: true },
      { text: 'is_user_auth.sso || is_user_auth.embedded', isComment: false, desc: 'is_user_auth.sso is a boolean that returns true only when the user is connected via SSO and is_user_auth.embedded is a boolean that returns true when the user is connected via the login page.' },
      { text: 'Tools specifications : How to correctly get submission_id', isComment: true },
      { text: 'var refReport = is_word ? submissionProps.submission_id : "{{requirement.answer.submissionProps.submission_id}}";',
        isComment: false,
        desc: 'This configuration ensures that we correctly obtain the submission_id, ' +
          'whether we are loading an HTML or Word page! \\n Note that the boolean variable is_word comes from the parameters ' +
          'of the function. Exemple : reports_html_word(is_word).' }
    ];

    const scriptInfoContent = (
      <div style={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
        <h3 style={{ color: '#007ad9', marginBottom: '0.5rem' }}>Script Structure</h3>
        <p style={{ color: '#555', marginBottom: '1rem' }}>Use the lines below to structure and manipulate data in your script. Click a line to copy it to the clipboard.</p>

        {scriptLines.map((line, index) => (
          line.isComment ? (
            <h4 key={index} style={{ color: '#007ad9', marginTop: '1rem' }}>{line.text}</h4>
          ) : (
            <div key={index} style={{ marginBottom: '1rem' }}>
              <p
                onClick={() => this.copyToClipboard(line.text)}
                style={{
                  cursor: 'pointer',
                  backgroundColor: '#f9f9f9',
                  padding: '8px',
                  borderRadius: '4px',
                  color: '#333',
                  marginBottom: '4px'
                }}
              >
                {line.text}
              </p>
              <small style={{ color: '#888' }}>{line.desc}</small>
            </div>
          )
        ))}

        <p style={{ marginTop: '1rem', fontStyle: 'italic', color: '#888' }}>
          Click an item to copy it to your clipboard.
        </p>
      </div>
    );


    this.leftContents = (
      <React.Fragment>
        <h2 className="editorTitle">Script editor</h2>
        <Button icon="pi pi-info-circle"
                onClick={this.showScriptInfoModal} className="align-right"/>
        <Dialog header="Information" visible={this.state.displayScriptInfo} style={{ width: '50vw' }}
                onHide={this.hideScriptInfoModal} dismissableMask={true}>
          {scriptInfoContent}
        </Dialog>
        <div className="toolbarCenterRequirement">
          <InputText
            value={this.state.editorScriptName}
            onChange={(e) => this.setState({ editorScriptName: e.currentTarget.value })}
            placeholder="Script name"
            style={{ width: '20rem' }}
            readOnly
          />
          <Button className={'p-button-text'}
                  id="isTitleEditable"
                  icon="pi pi-pencil"
                  onClick={() => this.setState({
                    displayRenameScript: true,
                    isRenameRequest: true,
                  })}
          />
          <div>
            <Dialog
              header="Rename Script file : "
              visible={this.state.displayRenameScript}
              modal
              style={{ width: '400px' }}
              footer={this._renderRenameWindowFooterForScript()}
              onHide={() => this.setState({ displayRenameScript: false })}
            >
              <div className="confirmation-content">
                <label style={{ width: '100px' }}>New name : </label>
                <InputText
                  value={this.state.editorScriptName}
                  onChange={(e) => {
                    this.setState({ editorScriptName: e.currentTarget.value })
                  }
                  }
                  placeholder="Template name"
                  style={{ width: '15rem' }}
                />
              </div>
            </Dialog>
          </div>

          <Tooltip target="#isTitleEditable" position="bottom">
            <p>
              The title is read-only by default. Click this button to switch it to edit mode and make it editable.
            </p>
          </Tooltip>
        </div>
      </React.Fragment>
    );
    this.rightContents = (
      <div className="action">
        { this._renderLogsButton() }
        <Button
          type="button"
          icon="pi pi-download"
          tooltip="Download script"
          tooltipOptions={{ position: 'left' }}
          style={{ backgroundColor: '#0087b7', borderColor: '#0087b7' }}
          className="p-button-danger"
          onClick={() => this.setState({
            downloadScript: true,
          }, () => {
            this.setState({
              downloadScript: false,
            });
          })}
        />
        <Button
          tooltip="Save script"
          tooltipOptions={{ position: 'left' }}
          icon="pi pi-save"
          className="p-button-danger"
          style={{ backgroundColor: '#e98b3a', borderColor: '#e98b3a' }}
          onClick={this.handleOneUpload}
        />
        <Button
          tooltip="Delete script"
          tooltipOptions={{ position: 'left' }}
          type="button"
          icon="pi pi-trash"
          className="p-button-danger"
          onClick={() => this.setState({
            deleteScript: true,
          }, () => {
            this.setState({
              deleteScript: false
            });
          })}
        />
      </div>
    );

    return (
      <div className="editor-script">
        <div className="sideBarJS">
          <CodeSidebarJS
            requestNavigation={this.requestNavigation}
            reloadScripts={this.state.refreshScriptsState}
            resetRefreshScriptsState={() => this.setState({ refreshScriptsState: false })}
            deletedScript={this.handleDeletedScript}
            actionDeleteScript={this.state.deleteScript}
            actionDownloadScript={this.state.downloadScript}
            loadFirstScript={this.loadFirstScript}
          />
          <Dialog
              header="Confirmation"
              visible={this.state.displayLeaveConfirmation}
              modal
              style={{ width: "350px" }}
              footer={this.renderLeavingPageDialog()}
              onHide={() => this.setState({ displayLeaveConfirmation: false })}
          >
            <div className="confirmation-content">
              <i
                  className="pi pi-exclamation-triangle p-mr-3"
                  style={{ fontSize: "2rem" }}
              />
              <span>Are you sure you want to leave this page? Unsaved changes will be lost.</span>
            </div>
          </Dialog>
        </div>
        <Toolbar left={this.leftContents} right={this.rightContents}/>
        <div className="workPlace">
          <Card /*title={"Code editor JS" this.formService.t(this.t, "ui_admin_editor_js_title", false) }*/>
            <Toast ref={(el) => (this.toast = el)}/>
            <div className="p-grid" id="scriptEditorJS">
              <div className="p-col-12 p-d-flex p-jc-center" id="editorAreaJS">
                {!this.state.isLoading && (
                  <Editor
                    height="100%"
                    defaultLanguage="javascript"
                    value={this.state.editorText}
                    onChange={this.handleEditorChange}
                    onMount={this.handleEditorDidMount}
                  />
                )}
              </div>


              <div className="p-col-12 p-d-flex p-jc-center" id="questionSelectorJS">
                <QuestionSelectorJS
                  questionSelected={this.selectQuestion}
                />
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
  }

}

export default withTranslation()(CodeEditorJS);
